import gql from 'graphql-tag'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLazyQuery } from '../api/graphql'
import {
  browserTimeZone,
  shouldConfirmCrossZone,
  shouldQueryResolvedZone,
} from '../util/crossZoneConfirm'

// Phase 10 (design rev.5 §5) — shared cross-zone save-confirmation gate, reused
// by EventNew (create) and EventEdit (edit). It (a) determines the zone the
// event WILL ACTUALLY BE STORED IN — server-resolved, never a client guess —
// and (b) gates submit on an explicit confirm when the browser zone differs.
//
// Zone source per path (TRUTHFULNESS invariant — the crux):
//   * CREATE: the Phase-7 `resolvedTimeZone(placeId:, primaryCommunityId:)`
//     query, which returns EXACTLY what EventCreate#resolve stores when no
//     zone is explicitly picked (proven parity via the shared
//     Events::ResolvedTimeZone.for_selection). Queried ONLY once a venue OR
//     community is actually selected — the no-selection footgun: that query
//     with neither id returns DEFAULT_TIMEZONE ("America/New_York"), a
//     resolved-LOOKING value, NOT nil, so a bare browserZone !== resolvedZone
//     would pop a spurious "Save as Eastern?" on every fresh form for
//     non-Eastern browsers. Until a selection drives the query, resolvedZone
//     stays null and shouldConfirmCrossZone returns false (no confirm). The
//     create path is BYTE-UNCHANGED by Phase 10b.
//   * EDIT (Phase 10b): the `editResolvedTimeZone(eventId, placeId:)` query —
//     which returns EXACTLY what EventUpdate#resolve STORES for the CURRENT
//     in-form venue, because BOTH it and EventUpdate delegate to the SAME
//     Events::ResolvedLeadZone (single source of truth; a mandatory parity
//     rspec proves they cannot drift). This ONE server value is truthful for
//     EVERY edit sub-case — geocoded venue (venue zone), blank-geo venue
//     (persisted column — the 10a residual, now closed), no/removed venue
//     (persisted column per Phase-9b), unchanged venue (same) — so the 10a
//     client-side editConfirmZoneSource branching is REMOVED (the server is
//     the single source). Queried on every edit (debounced) keyed by the
//     current in-form placeId; the loaded event always has a persisted
//     column, so a nil placeId is correct to query (server preserves the
//     column) — the no-selection footgun is a create-only concern.

const RESOLVED_TIME_ZONE_QUERY = gql`
  query ResolvedTimeZone($placeId: ID, $primaryCommunityId: ID, $tempPlaceAddressState: String, $tempPlaceAddressCity: String) {
    resolvedTimeZone(
      placeId: $placeId
      primaryCommunityId: $primaryCommunityId
      tempPlaceAddressState: $tempPlaceAddressState
      tempPlaceAddressCity: $tempPlaceAddressCity
    )
  }
`

// Phase 10b: the EDIT zone == what EventUpdate#resolve stores for the CURRENT
// in-form venue (same Events::ResolvedLeadZone the resolver uses — no drift).
const EDIT_RESOLVED_TIME_ZONE_QUERY = gql`
  query EditResolvedTimeZone($eventId: ID!, $placeId: ID, $tempPlaceAddressState: String, $tempPlaceAddressCity: String) {
    editResolvedTimeZone(
      eventId: $eventId
      placeId: $placeId
      tempPlaceAddressState: $tempPlaceAddressState
      tempPlaceAddressCity: $tempPlaceAddressCity
    )
  }
`

type Mode = 'create' | 'edit'

type Args = {
  mode: Mode
  // create-path selection (form model). A typed-but-not-geocoded venue has no
  // placeId -> the query falls back to community/default, which is exactly
  // what EventCreate stores for that case (consistent — confirm stays
  // truthful, just coarser). Do NOT "fix" that client-side.
  placeId?: string | null
  primaryCommunityId?: string | null
  // #898 item 1: the CURRENT typed (not-in-directory) temp-venue state/city.
  // A typed venue has no placeId, so its state/city is what the server resolves
  // the zone from (instead of falling back to community/default) — and the
  // confirm must query with it so it stays == what EventCreate stores.
  tempPlaceAddressState?: string | null
  tempPlaceAddressCity?: string | null
  // edit-path (Phase 10b): the persisted event's global id + the CURRENT
  // in-form placeId (tracked via EventEdit's <AutoForm> onChange; nil = no /
  // temp / removed venue). editResolvedTimeZone(eventId, placeId) returns
  // exactly what EventUpdate stores for that venue — truthful for every
  // sub-case incl. blank-geo (no client-side zone selection needed).
  eventId?: string | null
  currentPlaceId?: string | null
  // edit-path temp-venue (#898 item 1): the CURRENT in-form typed state/city.
  currentTempPlaceAddressState?: string | null
  currentTempPlaceAddressCity?: string | null
}

type ModalProps = {
  isOpen: boolean
  resolvedZone: string
  onConfirm: () => void
  onCancel: () => void
}

type Return = {
  // Wrap the real submit handler. The returned function decides per-call
  // whether to gate (open the confirm) or pass straight through.
  guardSubmit: (realSubmit: (doc: unknown) => void) => (doc: unknown) => void
  modalProps: ModalProps
  // #898 item 2: the SAME server-resolved zone the confirm uses (null until a
  // selection has driven a query — the no-selection footgun stays handled), so
  // the on-form Summary can show the venue zone DURING form-filling, not only
  // in the submit-time confirm. It is the identical truthful value; surfacing
  // it for display does not change gating.
  resolvedZone: string | null
  // CVL-119: true while a Submit is held waiting for the zone lookup (the forms
  // show "Checking time zone…" next to the button).
  checkingZone: boolean
}

// CVL-119: the longest a Submit is held for a lookup that never answers; after
// that it saves without the confirm, same as a failed lookup.
const ZONE_CHECK_TIMEOUT_MS = 5000

const useCrossZoneConfirm = ({
  mode,
  placeId,
  primaryCommunityId,
  tempPlaceAddressState,
  tempPlaceAddressCity,
  eventId,
  currentPlaceId,
  currentTempPlaceAddressState,
  currentTempPlaceAddressCity,
}: Args): Return => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  // The submit closure stashed while the confirm is shown.
  const pendingSubmit = useRef<(() => void) | null>(null)

  const onLookupError = useCallback(
    // Non-critical: a failed lookup must NEVER block submit (truthfulness >
    // gating — without a server zone we cannot substantiate a mismatch, so we
    // fail open by leaving resolvedZone unset). The server is authoritative
    // and stores correctly regardless.
    (error: { message: string }) => console.error('cross-zone confirm lookup failed:', error.message),
    [],
  )

  // CREATE: Phase-7 resolvedTimeZone (BYTE-UNCHANGED by 10b). EDIT (10b):
  // editResolvedTimeZone — what EventUpdate will store for the current venue.
  const [fetchCreateZone, { data: createData, loading: createLoading, error: createError }] = useLazyQuery(
    RESOLVED_TIME_ZONE_QUERY,
    {
      fetchPolicy: 'network-only',
      onError: onLookupError,
    },
  )
  const [fetchEditZone, { data: editData, loading: editLoading, error: editError }] = useLazyQuery(EDIT_RESOLVED_TIME_ZONE_QUERY, {
    fetchPolicy: 'network-only',
    onError: onLookupError,
  })

  // Issue the server-zone query (debounced so rapid form changes don't spam).
  //   * CREATE: Phase-7 query, ONLY once a venue OR community is selected
  //     (no-selection footgun: do NOT query / compare before then) — BYTE
  //     UNCHANGED.
  //   * EDIT (10b): editResolvedTimeZone(eventId, placeId: currentPlaceId) on
  //     every edit. The loaded event always has a persisted column, so a nil
  //     placeId is correct to query here (the server preserves the column via
  //     Phase-9b) — there is NO no-selection footgun on edit.
  //
  // Staleness guard: record the variables (venue, community, typed city/state)
  // each query is issued for. Apollo's data from a PRIOR selection would
  // otherwise be a stale zone for the CURRENT one while a new query is pending
  // — a truthfulness violation on rapid changes. The result is trusted ONLY
  // when it was fetched for the current variables and has landed (else null ->
  // no stale confirm; the pending query repopulates it). CVL-119: keyed on all
  // the variables, not just placeId, so a typed venue's new city/state counts
  // as pending too.
  const lookup = useMemo(() => {
    if (mode === 'create') {
      if (!shouldQueryResolvedZone(placeId, primaryCommunityId, tempPlaceAddressState)) {
        return null
      }
      const variables = {
        placeId: placeId || null,
        primaryCommunityId: primaryCommunityId || null,
        tempPlaceAddressState: tempPlaceAddressState || null,
        tempPlaceAddressCity: tempPlaceAddressCity || null,
      }
      return { key: JSON.stringify(variables), fire: () => fetchCreateZone({ variables }) }
    }
    if (!eventId) {
      return null
    }
    const variables = {
      eventId,
      placeId: currentPlaceId || null,
      tempPlaceAddressState: currentTempPlaceAddressState || null,
      tempPlaceAddressCity: currentTempPlaceAddressCity || null,
    }
    return { key: JSON.stringify(variables), fire: () => fetchEditZone({ variables }) }
  }, [
    mode,
    placeId,
    primaryCommunityId,
    tempPlaceAddressState,
    tempPlaceAddressCity,
    eventId,
    currentPlaceId,
    currentTempPlaceAddressState,
    currentTempPlaceAddressCity,
    fetchCreateZone,
    fetchEditZone,
  ])
  const lookupKey = lookup?.key ?? null

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const queriedForKey = useRef<string | null>(null)
  useEffect(() => {
    if (!lookup) {
      return
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      queriedForKey.current = lookup.key
      lookup.fire()
    }, 400)
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
    // Re-fire only when the variables change, not on a new `lookup` object.
  }, [lookupKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // The zone the confirm must state == what the save will ACTUALLY store.
  //   * CREATE: resolvedTimeZone result (null until a selection drove a query
  //     — footgun handled).
  //   * EDIT: editResolvedTimeZone result == what EventUpdate stores for the
  //     current venue (same Events::ResolvedLeadZone — no drift; truthful for
  //     geocoded / blank-geo / no-venue / unchanged alike).
  // CVL-119: `lookupPending` covers both the 400 ms debounce and the request in
  // flight. A failed lookup is not pending and resolves no zone (fail open).
  const loading = mode === 'create' ? createLoading : editLoading
  const lookupError = mode === 'create' ? createError : editError
  const lookupPending = lookupKey !== null && (queriedForKey.current !== lookupKey || loading)
  const rawZone =
    mode === 'create'
      ? (createData as { resolvedTimeZone?: string } | undefined)?.resolvedTimeZone ?? null
      : (editData as { editResolvedTimeZone?: string } | undefined)?.editResolvedTimeZone ?? null
  const resolvedZone: string | null = lookupKey === null || lookupPending || lookupError ? null : rawZone

  const browserZone = browserTimeZone()
  const needsConfirm = shouldConfirmCrossZone({ browserZone, resolvedZone })

  // Gate a submit: open the confirm when the zones differ, else save.
  const gate = useCallback(
    (run: () => void) => {
      if (!needsConfirm) {
        // In-zone majority, failed lookup, or unknown browser zone: submit
        // straight through (additive — unchanged behavior).
        run()
        return
      }
      // Cross-zone: gate. Stash the real submit; it runs only on confirm.
      pendingSubmit.current = run
      setConfirmOpen(true)
    },
    [needsConfirm],
  )

  // A Submit clicked while the lookup is pending is held here (with its 5 s
  // cap) and gated once the lookup lands, so nobody clicks twice. It remembers
  // the lookup key it was held for: the click saved the form as it was then, so
  // a venue change while it waits drops it rather than checking it against the
  // new venue's zone.
  const heldSubmit = useRef<{ run: () => void; key: string | null; timer: ReturnType<typeof setTimeout> } | null>(null)
  const [checkingZone, setCheckingZone] = useState(false)

  const releaseHeld = useCallback(() => {
    const held = heldSubmit.current
    heldSubmit.current = null
    setCheckingZone(false)
    if (held) {
      clearTimeout(held.timer)
    }
    return held
  }, [])

  useEffect(() => {
    const held = heldSubmit.current
    if (!held) {
      return
    }
    if (held.key !== lookupKey) {
      releaseHeld()
      return
    }
    if (lookupPending) {
      return
    }
    releaseHeld()
    gate(held.run)
  }, [lookupKey, lookupPending, gate, releaseHeld])

  // A form that closes drops its held submit.
  useEffect(
    () => () => {
      if (heldSubmit.current) {
        clearTimeout(heldSubmit.current.timer)
        heldSubmit.current = null
      }
    },
    [],
  )

  // The forms call guardSubmit after awaiting the app-build check, so the
  // function they hold can be from an earlier render than the one the lookup
  // landed in. It reads the lookup state from this ref, which every render
  // refreshes, instead of its own render's values.
  const latest = useRef({ lookupKey, lookupPending, gate })
  latest.current = { lookupKey, lookupPending, gate }

  const guardSubmit = useCallback(
    (realSubmit: (doc: unknown) => void) => (doc: unknown) => {
      const run = () => realSubmit(doc)
      const current = latest.current
      if (!current.lookupPending) {
        current.gate(run)
        return
      }
      if (heldSubmit.current) {
        clearTimeout(heldSubmit.current.timer)
      }
      // A lookup that never answers saves after the cap, like a failed one.
      const timer = setTimeout(() => {
        releaseHeld()?.run()
      }, ZONE_CHECK_TIMEOUT_MS)
      heldSubmit.current = { run, key: current.lookupKey, timer }
      setCheckingZone(true)
    },
    [releaseHeld],
  )

  const onConfirm = useCallback(() => {
    setConfirmOpen(false)
    const run = pendingSubmit.current
    pendingSubmit.current = null
    if (run) {
      run()
    }
  }, [])

  const onCancel = useCallback(() => {
    setConfirmOpen(false)
    pendingSubmit.current = null
  }, [])

  return {
    guardSubmit,
    modalProps: {
      isOpen: confirmOpen,
      resolvedZone: resolvedZone || '',
      onConfirm,
      onCancel,
    },
    resolvedZone,
    checkingZone,
  }
}

export default useCrossZoneConfirm
